/**
 * Application bootstrap.
 *
 * The main job of this module is to run a two stage initialisation, pre and post the DOM being ready.
 * Normally the body of this module *should* be executed before the DOM is ready, but we cannot
 * guarantee that.
 *
 * @module app/app
 */

import _ from 'lodash';
import animationFrame from 'animation-frame';
import jQuery from 'jquery';
import docCookies from 'doc-cookies';
import moment from 'moment-timezone';
import './components/PreLoaderManager';
import basePage from './pages/BasePage';
import BetslipListener from './components/BetslipListener';
import CarouselHandler from './components/CarouselHandler';
import DeviceDetector from './components/DeviceDetector';
import MarketingEvent from './components/MarketingEvent';
import DOMPostRenderManager from './components/DOMPostRenderManager';
import Feedback from './components/FeedbackComponent';
import GoldenRace from './components/GoldenRace';
import HashListener from './controllers/HashListener';
import MessageBusTopics from '../common/MessageBusTopics';
import NativeAppBridge from './components/NativeAppBridge';
import pageController from './controllers/PageController';
import Router from './controllers/Router';
import SideMenu from './components/SideMenu';
import TopBetsLoader from './components/TopBetsLoader';
import Translator from './components/Translator';
import { get } from '../utils';
import { hidePagePreloader, initialisePagePreloader } from './libs/PagePreloaderHelper';
import FeatureFlagsHandler from './libs/FeatureFlags';
import CashierHandler from './components/CashierHandler';
import * as tooltip from 'sportsbook-tooltip';
import '@sb/accas/dist/accas.widget';
import '@sb/accas-football/dist/accas-football.widget';
import '@sb/popular-bet-builders/dist/popular-bet-builders.widget';

import(/* webpackChunkName: "welcomeMessage" */'./components/WelcomeMessage');

// jQuery appends a cache bursting query parameter to scripts by default,
// which we need to avoid if we want to properly use our cdn
jQuery.ajaxSettings.cache = true;

window.$ = window.$$ = window.jQuery = jQuery;
window._ = _;
window.moment = moment;
window.docCookies = docCookies;
window.AnimationFrame = animationFrame;

const RACING_ACCA_HOME = 'accas-hub-racing-home-component';
const FOOTBALL_ACCA_HOME = 'accas-hub-football-home-component';

const POPULAR_BET_BUILDERS_HUB = 'popular-bet-builder-hub-component-football';

const HOME_PAGE_URL = '/betting/en-gb';
const RACING_MEETINGS_URL = '/betting/en-gb/horse-racing/meetings';
const NEXT_OFFS_CONTAINER = '[data-panel="panel-next-off"]';
const FOOTBALL_ACCA_CONTAINER = 'match-highlights-OB_SP9';

const ACCA_MARGIN = '-8px';

let accaRacingHomeStarted = false;
let betBuilderHomeStarted = false;
let accaRacingMeetingStarted = false;
const isBBon = !!window.localStorage.getItem('isBBon');

/**
 * Start the application.
 *
 * @returns {void}
 *
 * @private
 */
const start = function () {
    const pagePreloaderTimeoutID = initialisePagePreloader();

    if (DeviceDetector.isNativeItaly() || DeviceDetector.isNativeSpain()) {
        jQuery('#toolbar').find('#slots').remove();
    }
    basePage.init();
    Router.start();
    HashListener.start();
    BetslipListener.init();
    FeatureFlagsHandler.init();

    NativeAppBridge.init();
    if (get(window, 'whStatic.config.topBets.enabled')) {
        TopBetsLoader.init();
    }
    SideMenu.init();
    CarouselHandler.init();
    DOMPostRenderManager.init();
    Feedback.init();
    GoldenRace.init();

    window.WH.messageBus.publishSticky(MessageBusTopics.REMOVE_WEB_SKELETON_OVERLAY, true);

    if (DeviceDetector.isNative() && DeviceDetector.isIOS()) {
        window.WH.messageBus.publishSticky(MessageBusTopics.NATIVE_REMOVE_PLACEHOLDER, true);
    }
    appendDivToPanelNextOff();
    appendDivToPanelFootball();
    if(isBBon) appendDivToPanelBetBuilderFootball(); 

    clearTimeout(pagePreloaderTimeoutID);
    hidePagePreloader();
    MarketingEvent.init();
    CashierHandler.init();

    observeUrlChange();
};


/**
 * What to do when the DOM is ready
 *
 * @returns {void}
 *
 * @private
 */
const afterDOMReady = function () {
    Translator.init();

    pageController({
        router: Router.router,
    });

    window.WH.messageBus.publishSticky(MessageBusTopics.APP_JS_DOM_READY);

    start();
};

const checkIfCountryIsAllowed = () => {
    const allowedCountries = ['GB', 'IE', 'GI'];
    const match = document.cookie.match(/(?:^|;\s*)country=([^;]*)/);
    return match ? allowedCountries.includes(match[1]) : false;
};

const messageForAcca = (selector, topic, sport, id) => {
    window.WH.messageBus.publish(topic, {
        selector: selector,
        id,
        metadata: {},
        sport,
    });
};
const startAccaWidget = () => {
    setTimeout(() => {
        window.consoleReference.log('ACCA Widget start racing');
        messageForAcca(RACING_ACCA_HOME, 'wh.sb.widget.start', 'horse-racing', 'ACCAS_HUB');
        accaRacingHomeStarted = true;
    }, 0);
};

const startAccaWidgetFootball = () => {
    setTimeout(() => {
        window.consoleReference.log('ACCA Widget start football');
        messageForAcca(FOOTBALL_ACCA_HOME, 'wh.sb.widget-acca-football.start',
            'football', 'ACCAS_HUB_FOOTBALL');
    }, 0);
};

const startBetBuilderWidgetFootball = () => {
    setTimeout(() => {
        window.consoleReference.log('ACCA Widget start football');
        messageForAcca(POPULAR_BET_BUILDERS_HUB, 'wh.sb.widgetPopularBetBuilders.start',
            'football', 'POPULAR_BET_BUILDERS_HUB');
    }, 0);
};

const stopAccaWidget = () => {
    setTimeout(() => {
        messageForAcca(RACING_ACCA_HOME, 'wh.sb.widget.stop', 'horse-racing', 'ACCAS_HUB');
        messageForAcca(FOOTBALL_ACCA_HOME, 'wh.sb.widget-acca-football.stop',
            'football', 'ACCAS_HUB_FOOTBALL');
        messageForAcca(POPULAR_BET_BUILDERS_HUB, 'wh.sb.widgetPopularBetBuilders.stop',
            'football', 'POPULAR_BET_BUILDERS_HUB');

        accaRacingHomeStarted = false;
        betBuilderHomeStarted = false;
    }, 0);
};

function appendDivToPanelNextOff() {
    const parentDiv = document.querySelector(NEXT_OFFS_CONTAINER);

    if (!parentDiv) {
        return;
    }

    const accaContainer = document.createElement('div');
    accaContainer.id = RACING_ACCA_HOME;
    accaContainer.style.margin = ACCA_MARGIN;
    parentDiv.insertAdjacentElement('afterend', accaContainer);
}

function appendDivToPanelBetBuilderFootball() {
    const parentDiv = document.getElementById(FOOTBALL_ACCA_CONTAINER);

    if (!parentDiv) {
        return;
    }

    let accaContainer = document.getElementById(POPULAR_BET_BUILDERS_HUB);
    if (accaContainer) return;

    accaContainer = document.createElement('div');
    accaContainer.id = POPULAR_BET_BUILDERS_HUB;
    accaContainer.style.margin = ACCA_MARGIN;

    const parentDivFirstChild = parentDiv.querySelector('div:first-child');

    if (parentDivFirstChild) {
        parentDivFirstChild.insertAdjacentElement('afterend', accaContainer);
    } else {
        parentDiv.appendChild(accaContainer);
    }
}

function appendDivToPanelFootball() {
    const parentDiv = document.getElementById(FOOTBALL_ACCA_CONTAINER);

    if (!parentDiv) {
        return;
    }

    let accaContainer = document.getElementById(FOOTBALL_ACCA_HOME);
    if (accaContainer) return;

    accaContainer = document.createElement('div');
    accaContainer.id = FOOTBALL_ACCA_HOME;
    accaContainer.style.margin = ACCA_MARGIN;

    const parentDivFirstChild = parentDiv.querySelector('div:first-child');

    if (parentDivFirstChild) {
        parentDivFirstChild.insertAdjacentElement('afterend', accaContainer);
    } else {
        parentDiv.appendChild(accaContainer);
    }
}

function checkUrl(callback) {
    const pushState = history.pushState;
    const replaceState = history.replaceState;

    function onUrlChange() {
        callback(window.location.pathname);
    }

    history.pushState = function (...args) {
        pushState.apply(this, args);
        onUrlChange();
    };

    history.replaceState = function (...args) {
        replaceState.apply(this, args);
        onUrlChange();
    };

    window.addEventListener('popstate', onUrlChange);
}

function observeUrlChange() {
    const actualUrl = window.location.pathname;
    if (actualUrl == HOME_PAGE_URL) {
        if (checkIfCountryIsAllowed()) {
            startAccaWidget();
            startAccaWidgetFootball();
        }
        if(isBBon && checkIfCountryIsAllowed()) {
            startBetBuilderWidgetFootball();
        }
    }

    checkUrl((newUrl) => {
        if (newUrl == HOME_PAGE_URL) {
            if (checkIfCountryIsAllowed()) {
                startAccaWidget();
                startAccaWidgetFootball();
            }
            if(isBBon && checkIfCountryIsAllowed()) {
                startBetBuilderWidgetFootball();
            }
        } else {
            if (!newUrl.startsWith(RACING_MEETINGS_URL) || accaRacingHomeStarted) {
                stopAccaWidget();
            }

        }
    });
}

/**
 * Methods to be public
 *
 * @type {Object}
 */
const api = {
    /**
     * Starts application
     *
     * @returns {void}
     *
     * @public
     */
    start: function () {
        jQuery(afterDOMReady);
    },
};


api.start();


export default api;
